import axios from 'axios';
import { handleAuthFailure, refreshAccessTokenWithDedup } from './authErrorHandler';
import { handleHttpError } from './httpErrorHandler';

// Type definitions for axios - wrapper to provide generic type support
export type AxiosInstance = ReturnType<typeof axios.create>;
export type AxiosResponse<T = any> = {
  data: T;
  status: number;
  statusText: string;
  headers: Record<string, string>;
  config: any;
};
type AxiosError = any;
export type AxiosRequestConfig = any & {
  _retry?: boolean;
  /** Skip global error handling and toast notification for this request */
  silent?: boolean;
};

// ApiClient class to encapsulate axios instance and methods
class ApiClient {
  private http: AxiosInstance;
  private readonly baseURL: string;

  constructor() {
    // Use relative path for production (via nginx proxy), or env variable for development
    this.baseURL = process.env.REACT_APP_API_URL || '';

    this.http = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token
    this.http.interceptors.request.use(
      (config: any) => {
        const token = localStorage.getItem('access_token');
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle errors and token refresh
    this.http.interceptors.response.use(
      (response: any) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig;

        // Handle 403 Forbidden errors with token refresh
        if (error.response?.status === 403 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            // Use the centralized auth error handler for token refresh
            const newToken = await refreshAccessTokenWithDedup(async () => {
              const refreshToken = localStorage.getItem('refresh_token');
              if (!refreshToken) {
                throw new Error('No refresh token available');
              }

              // Create a separate axios instance for token refresh to avoid recursive interceptor calls
              const refreshApi = axios.create({
                baseURL: this.baseURL,
                headers: {
                  'Content-Type': 'application/json',
                },
              });

              const response = await refreshApi.post('/api/v1/auth/refresh', {
                refresh_token: refreshToken,
              });

              return response.data as {
                access_token: string;
                refresh_token?: string;
              };
            });

            // Update the Authorization header in the original request
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }

            // Retry the original request
            return this.http(originalRequest);
          } catch (refreshError: any) {
            // Token refresh failed - check if it's a 401 error indicating expired refresh token
            if (refreshError?.response?.status === 401) {
              handleAuthFailure(
                '401 Invalid token: Signature has expired - refresh token also expired'
              );
            }
            return Promise.reject(refreshError);
          }
        }

        // Handle 401 Unauthorized errors - show toast AND use auth handler
        if (error.response?.status === 401) {
          // Show toast notification for 401 errors
          const isSilent = (error.config as any)?.silent === true;
          handleHttpError(error, { silent: isSilent });
          // Also trigger auth failure handler for redirect/logic
          handleAuthFailure('401 Unauthorized - token expired or invalid');
          return Promise.reject(error);
        }

        // Handle other HTTP errors with unified toast notification
        // This will display 422, 400, 404, 500, etc. errors as toast notifications
        // Skip if request was marked as silent (for custom error handling)
        const isOtherSilent = (error.config as any)?.silent === true;
        handleHttpError(error, { silent: isOtherSilent });

        return Promise.reject(error);
      }
    );
  }

  get<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.http.get<T>(url, config) as unknown as Promise<AxiosResponse<T>>;
  }

  post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.http.post<T>(url, data, config) as unknown as Promise<AxiosResponse<T>>;
  }

  put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.http.put<T>(url, data, config) as unknown as Promise<AxiosResponse<T>>;
  }

  patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.http.patch<T>(url, data, config) as unknown as Promise<AxiosResponse<T>>;
  }

  delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.http.delete<T>(url, config) as unknown as Promise<AxiosResponse<T>>;
  }
}

// Create and export a singleton instance
const api = new ApiClient();

export default api;
